import { createFileRoute, Link } from '@tanstack/react-router';
import { FormattedMessage } from 'react-intl';
import { z } from 'zod';

import { useTheme } from '@/components/providers/ThemeProvider';

export const Route = createFileRoute('/(auth)/slack-not-linked/')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown> | undefined) =>
    z
      .object({
        workspace: z.string().optional(),
      })
      .parse(search),
});

function RouteComponent() {
  const { resolvedTheme } = useTheme();
  const search = Route.useSearch();
  const { workspace } = search;

  return (
    <section className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-red-100 dark:from-gray-900 dark:via-yellow-900 dark:to-red-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-yellow-400 to-orange-600 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-orange-400 to-red-600 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-gradient-to-br from-yellow-300 to-orange-400 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse delay-500"></div>
      </div>

      <div className="relative z-10 flex flex-col items-center justify-center px-6 py-8 mx-auto md:h-screen lg:py-0">
        <div className="flex items-center mb-8 text-2xl font-semibold text-gray-900 dark:text-white animate-fade-in">
          <img
            className="mr-2 drop-shadow-lg"
            src={resolvedTheme === 'dark' ? '/LogoWhite.svg' : '/LogoBlack.svg'}
            alt="logo"
            width={140}
            height={140}
          />
        </div>

        <div className="w-full bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 dark:bg-gray-800/80 dark:border-gray-700/50 md:mt-0 sm:max-w-md xl:p-0 transform transition-all duration-500 hover:shadow-3xl hover:scale-105 animate-slide-up">
          <div className="p-8 space-y-6 text-center">
            <div className="text-center">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-yellow-600 to-orange-600 dark:from-yellow-400 dark:to-orange-400 bg-clip-text text-transparent md:text-3xl">
                <FormattedMessage id="auth.slackNotLinked.title" />
              </h1>
              <div className="mt-2 h-1 w-16 bg-gradient-to-r from-yellow-500 to-orange-500 dark:from-yellow-400 dark:to-orange-400 rounded-full mx-auto"></div>
            </div>

            <div className="space-y-4">
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800">
                <p className="text-gray-700 dark:text-gray-300 font-medium">
                  <FormattedMessage
                    id="auth.slackNotLinked.welcomeMessage"
                    values={{ workspace: workspace ?? '' }}
                  />
                </p>
              </div>

              <p className="text-gray-600 dark:text-gray-400">
                <FormattedMessage id="auth.slackNotLinked.description" />
              </p>

              <div className="pt-4">
                <Link
                  to="/sign-in"
                  className="group relative w-full inline-flex items-center justify-center px-6 py-3 text-white bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 rounded-lg font-semibold text-sm shadow-lg hover:shadow-xl transform transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-yellow-300 dark:focus:ring-yellow-800"
                >
                  <FormattedMessage id="auth.slackNotLinked.signInCta" />
                  <svg
                    className="ml-2 w-4 h-4 transform group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
